/**
 * <bh-print-toggle> — "what should we do with the photo?" in the reference-photo block
 * (snippets/cake-block-image-upload.liquid): Print it (+€10) / Draw it (+€3/5) / I'll leave instructions.
 *
 * Why: people upload a photo wanting it PRINTED and nothing told them they could; others upload
 * photos (often AI) that the team had to ask about after purchase every time. One question,
 * because the audience is not technical (2026-10-11: a switch + follow-up was too much).
 *
 * Hidden until a photo is attached, or until print is ticked in the add-ons list.
 *
 * Print and Draw ARE add-ons (`data-addon` = `edible-print` / `drawing-2-colors`): picking one
 * ticks that row's checkbox and dispatches `change`, so the configurator's own handler owns
 * state, price and the cart line; ticking a row in the list selects its radio. The two untick
 * each other (a print replaces the piped drawing). "I'll leave instructions" opens the notes
 * block (cake-block-notes.liquid) and focuses #CakeNotes; it is then required. The radios carry
 * no properties[] name: the add-ons and the note are what reaches the order.
 *
 * window.bhPrint.missing() is called from validateConfiguration (bakehunters-configurator.js).
 * Rules: docs/business-rules.md → "Photo print".
 */
(function () {
  const PRINT = 'edible-print';
  const DRAWING = 'drawing-2-colors';

  const box = (handle) => document.querySelector(`.bh-addon-row[data-handle="${handle}"] .bh-addon-checkbox`);
  const ticked = (handle) => !!(box(handle) && box(handle).checked);
  const setAddon = (handle, on) => {
    const cb = box(handle);
    if (!cb || cb.checked === on) return;
    cb.checked = on;
    cb.dispatchEvent(new Event('change', { bubbles: true }));
  };

  class BhPrintToggle extends HTMLElement {
    connectedCallback() {
      this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
      this.notesRadio = this.radios.find((r) => r.value === 'notes');
      this.notes = document.getElementById('CakeNotes');
      this.file = document.getElementById('CakeReferenceImage');
      this.details = this.closest('details');

      document.addEventListener('change', (e) => this.onChange(e));
      // Clicks cover what fires no `change`: the upload's ✕ (empties the input) and the size
      // pills (re-price the add-ons, €3 vs €5). Deferred so the configurator has re-rendered.
      document.addEventListener('click', () => setTimeout(() => this.sync()));
      // Add-on rows are rendered by the configurator after this element connects, and a cart
      // edit or URL prefill can pre-tick an add-on.
      window.addEventListener('load', () => this.sync());
      // The photo or notes block was removed (bh-optional-remove.js): a print needs the photo,
      // and "I'll leave instructions" with the instructions gone has to be answered again.
      document.addEventListener('bh:optional-removed', (e) => {
        if (e.detail.kind === 'photo') setAddon(PRINT, false);
        if (e.detail.kind === 'notes' && this.notesRadio) this.notesRadio.checked = false;
        this.sync();
      });
      window.bhPrint = this;
    }

    onChange(e) {
      if (this.radios.includes(e.target)) {
        this.radios.forEach((r) => r.dataset.addon && setAddon(r.dataset.addon, r === e.target));
        if (e.target === this.notesRadio) this.openNotes();
      }
      const cb = e.target.closest && e.target.closest('.bh-addon-checkbox');
      if (cb && cb.checked) {
        if (cb.value === PRINT) {
          setAddon(DRAWING, false);
          if (this.details) this.details.open = true; // ticked in the list: show where the photo goes
        }
        if (cb.value === DRAWING) setAddon(PRINT, false);
      }
      this.sync();
    }

    hasPhoto() {
      return !!(this.file && this.file.files && this.file.files.length);
    }

    sync() {
      // An add-on ticked anywhere wins the radio; an add-on radio with its add-on unticked clears.
      const onAddon = this.radios.find((r) => r.dataset.addon && ticked(r.dataset.addon));
      if (onAddon) onAddon.checked = true;
      else this.radios.forEach((r) => { if (r.dataset.addon && r.checked) r.checked = false; });

      this.hidden = !(ticked(PRINT) || this.hasPhoto());

      // Same price the add-on row shows, which the configurator keeps per size tier.
      this.radios.forEach((r) => {
        const out = r.closest('label').querySelector('[data-bh-price]');
        const price = r.dataset.addon && document.querySelector(`.bh-addon-row[data-handle="${r.dataset.addon}"] .addon-price-dynamic`);
        if (out && price) out.textContent = `+${parseFloat(price.textContent)} €`;
      });
    }

    openNotes() {
      if (!this.notes) return;
      const details = this.notes.closest('details');
      if (details) details.open = true;
      this.notes.focus({ preventScroll: true });
      this.notes.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // { el, flashEl, message } for the configurator's flagMissing, or null.
    missing() {
      this.sync();
      if (ticked(PRINT) && !this.hasPhoto()) {
        if (this.details) this.details.open = true;
        const drop = this.details?.querySelector('[data-bh-drop]');
        return { el: drop || this, flashEl: drop, message: this.dataset.errPhoto };
      }
      if (this.hasPhoto() && !this.radios.some((r) => r.checked)) {
        // el is the fieldset itself, so the scroll centres the question, not the whole tall block.
        const q = this.querySelector('fieldset');
        return { el: q, flashEl: q.querySelector('.bh-radio-group'), message: this.dataset.errChoice };
      }
      if (this.hasPhoto() && this.notesRadio?.checked && this.notes && !this.notes.value.trim()) {
        const details = this.notes.closest('details');
        if (details) details.open = true;
        return { el: this.notes, flashEl: this.notes, message: this.dataset.errNotes };
      }
      return null;
    }
  }

  if (!customElements.get('bh-print-toggle')) customElements.define('bh-print-toggle', BhPrintToggle);
})();
